import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import {
  CalendarCheck, GoogleLogo, MicrosoftOutlookLogo, AppleLogo, X, ArrowRight,
} from "@phosphor-icons/react";

/**
 * SyncCalendarNudgeCard — top-of-dashboard prompt for Pro owners who
 * have NOT yet connected any of the three calendar integrations
 * (Google, Outlook, Apple feed).
 *
 * Visibility rules:
 *   - Only Pro owners (free owners shouldn't be teased with Pro-only stuff)
 *   - Hidden if the owner already has ANY calendar connection
 *   - Hidden if the owner clicked Dismiss in the last 7 days (localStorage)
 *
 * Action: scrolls smoothly to the Calendar sync section and auto-opens the
 * "How calendar sync works" drawer so a brand-new Pro lands on the
 * walkthrough — no hunting around the dashboard.
 */
const DISMISS_KEY = "tt:sync_nudge_dismissed_at";
const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function isSnoozed() {
  try {
    const v = localStorage.getItem(DISMISS_KEY);
    if (!v) return false;
    const ts = parseInt(v, 10);
    return Number.isFinite(ts) && Date.now() - ts < SNOOZE_MS;
  } catch {
    return false;
  }
}

export default function SyncCalendarNudgeCard() {
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Defer all state-setting to next microtask so the effect body
    // itself stays side-effect-free (satisfies set-state-in-effect rule).
    queueMicrotask(async () => {
      if (cancelled) return;
      if (isSnoozed()) {
        if (!cancelled) setDismissed(true);
        return;
      }
      try {
        const { data } = await api.get("/me/calendar-sync-status");
        if (!cancelled) setStatus(data);
      } catch {
        if (!cancelled) setStatus(null);
      }
    });
    return () => { cancelled = true; };
  }, []);

  if (dismissed) return null;
  if (!status) return null;
  if (!status.is_pro) return null;
  if (status.has_any) return null;

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_) { /* private mode */ }
    setDismissed(true);
  };

  const handleConnect = () => {
    const target = document.querySelector('[data-testid="calendar-howto-trigger"]');
    const section = target?.closest("div");
    section?.scrollIntoView({ behavior: "smooth", block: "start" });
    setTimeout(() => {
      const trigger = document.querySelector('[data-testid="calendar-howto-trigger"]');
      if (trigger && typeof trigger.click === "function") trigger.click();
    }, 450);
  };

  return (
    <section
      className="relative rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/[0.06] via-card to-card p-5 sm:p-6 overflow-hidden mb-6"
      data-testid="sync-calendar-nudge-card"
    >
      <button
        type="button"
        onClick={dismiss}
        className="absolute top-3 right-3 rounded-full p-1 text-muted-foreground hover:bg-muted/60 hover:text-foreground transition"
        aria-label="Dismiss for 7 days"
        data-testid="sync-calendar-nudge-dismiss"
      >
        <X weight="bold" className="h-4 w-4" />
      </button>

      <div className="grid sm:grid-cols-[1fr_auto] gap-4 items-center pr-6">
        <div>
          <div className="inline-flex items-center gap-1.5 px-2 h-5 rounded-full bg-primary/15 text-primary text-[10px] uppercase tracking-wider font-bold">
            <CalendarCheck weight="fill" className="h-3 w-3" />
            Pro perk · unused
          </div>
          <h3 className="mt-2 font-display font-semibold text-lg sm:text-xl tracking-tight">
            Stop double-entering your schedule.
          </h3>
          <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed max-w-2xl">
            You already pay for Pro — and one of the biggest perks is sitting
            idle on your account. Connect <strong className="text-foreground">Google Calendar</strong>,
            <strong className="text-foreground"> Outlook</strong>, or generate
            an <strong className="text-foreground">Apple Calendar feed</strong> URL so
            stops you book in your calendar publish to TruckTrackr automatically.
            Setup takes about 60 seconds.
          </p>
          <div className="mt-3 flex items-center gap-3 text-[11px] text-muted-foreground">
            <span className="inline-flex items-center gap-1"><GoogleLogo weight="duotone" className="h-3.5 w-3.5 text-primary" /> Google</span>
            <span className="inline-flex items-center gap-1"><MicrosoftOutlookLogo weight="duotone" className="h-3.5 w-3.5 text-primary" /> Outlook</span>
            <span className="inline-flex items-center gap-1"><AppleLogo weight="duotone" className="h-3.5 w-3.5 text-primary" /> Apple</span>
          </div>
        </div>
        <Button
          type="button"
          onClick={handleConnect}
          className="bg-primary text-primary-foreground hover:bg-primary/90 h-11 rounded-full px-5 gap-1.5"
          data-testid="sync-calendar-nudge-cta"
        >
          <CalendarCheck weight="bold" className="h-4 w-4" />
          Show me how
          <ArrowRight weight="bold" className="h-4 w-4" />
        </Button>
      </div>
    </section>
  );
}
